// src/lib/transferWork.js
//
// #689's second half (gate 665 D19) and #699 (gate 665 D28), Session 79.22b.5.1.
// WHAT TRANSFER WORK IS WAITING, AND WHICH OF IT IS THIS PERSON'S.
//
// ── THE PROBLEM, in plain terms ──────────────────────────────────────────────
//
// Owen makes stakes at Houston. To sign off forty of them he opened the menu,
// tapped Transfers, landed on a tab, and then found his card -- five taps
// against Stede's two (#689). D18 made Transfers open on his job. D19 puts the
// job on his front door: Stock Home shows "Transfer work · 4 to make · 1 to
// load", and each chip opens Transfers on that tab, so the card's button is
// the second tap.
//
// ── WHY THIS IS ONE MODULE ───────────────────────────────────────────────────
//
// The gate's own condition: "It uses the same counting rules as the tab
// badges, so the two always agree." A shortcut saying "4 to make" over a tab
// saying 3 is a screen nobody can trust. So the rules for each tab's list live
// HERE, built from the same helpers the page has always used, and BOTH the
// Transfers page and Stock Home call them. The page passes the branches it is
// looking at; Stock Home passes the person's own branch. A second copy of
// these rules is exactly the drift #512 and #628 were.
//
// ADVISORY ONLY, everywhere in this file. A count or a chip grants nothing:
// every action still asks the database, which refuses anybody it must.

import { supabase } from './supabase';
import { isApprovable, productionQueue, isOwnShelf, transfersInHand } from './branchTransfers';
import { cutOff } from './readLimit';
import {
  hasReadyToLoad, shelfFillable, toCollectFor, arrivingAt, movingElsewhere,
  owedToBranch, shortFromBranch, sortByPromised, drivesHere,
  fetchOpenLoads, transferIdsWithLoadsToWatch, fetchBranchDrivers,
} from './transferLoads';
import { productionRows, reserveCandidates, fetchProductionData } from './production';
import { canApproveTransfer, canCloseTransferShort } from './canApprove';
import { canSignOffProduction, canStateHowFilled, isAdminManagerOrExecutive } from './permissions';

// ── #697, gate 665 D23, Session 79.22b.5.2 -- A HUB'S OWN GOODS ARE COLLECTED
// AT THE HUB BY THE BRANCH THEY ARE FOR ───────────────────────────────────────
//
// Dallas asks Austin for 6 frames off Austin's own shelf. Austin is the hub,
// where Dallas's driver already goes on transfer days, so the driver takes them
// there (Colby, D23: "dallas picks it up"; R2 at 79.22b.5.2: any branch the hub
// sends to, while its hub switch is on). So what is READY at a hub for another
// branch is that branch's To collect job, not the hub's To load job: the hub's
// card says so and offers no Send, and it is not counted as the hub's work --
// a number counting a job nobody there can do is #4's lesson. The database
// agrees: 20261001_hub_origin_collect_697.sql and _ledger_697.sql.
//
// THE SHEET IS LEFT ALONE, deliberately. Somebody at the hub who opens the
// transfer can still send it themselves, which is the way out for the day the
// hub does drive one, and for a made-to-order line a manager fills from the
// shelf (#628's route) - a manager's decision about the order, not a pickup.
export function hubIdsOf(branches) {
  return (Array.isArray(branches) ? branches : []).filter(b => b && b.is_transfer_hub).map(b => b.id);
}

export function collectedAtHub(transfer, hubIds = []) {
  if (!transfer || transfer.from_branch_id == null || transfer.to_branch_id == null) return false;
  if (String(transfer.from_branch_id) === String(transfer.to_branch_id)) return false;
  return (Array.isArray(hubIds) ? hubIds : []).some(id => String(id) === String(transfer.from_branch_id));
}

const readyAtHub = (t, hubIds) => collectedAtHub(t, hubIds) && hasReadyToLoad(t, t.branch_transfer_items || []);

// The receiving branch's job: everything ready at a hub that sent it to them.
// `branchId` null means every branch.
export function hubPickupsFor(transfers, branchId, hubIds = []) {
  return (Array.isArray(transfers) ? transfers : []).filter(t => readyAtHub(t, hubIds)
    && (branchId == null || String(t.to_branch_id) === String(branchId)));
}

// The hub's side of the same thing: drawn on its To load tab, never counted.
export function awaitingCollectionAt(transfers, branchId, hubIds = []) {
  return (Array.isArray(transfers) ? transfers : []).filter(t => readyAtHub(t, hubIds)
    && (branchId == null || String(t.from_branch_id) === String(branchId)));
}

// A pickup at a hub drawn as the To collect card already draws a pile at a yard
// (gate 665 Screen 10's right-hand card): the transfer's number where a truck's
// would be, and its lines as the pile. sortByPromised() reads the due date off
// `branch_transfers`, as it does for a truck.
export function asHubPickup(transfer) {
  return {
    id: `hub-${transfer.id}`,
    transfer_id: transfer.id,
    load_ref: transfer.transfer_number || `TRF-${transfer.id}`,
    from_branch_id: transfer.from_branch_id,
    to_branch_id: transfer.to_branch_id,
    branch_transfers: transfer,
    __hubPickup: true,
  };
}

// ── EACH TAB'S LIST, as the Transfers page has always built it ──────────────

// To load: something standing at the sending branch with a truck still to
// fill -- or, for the people who say how a line was filled, a made-to-order
// request the sending branch's SHELF can fill (#628 half 2). `branchId` null
// means every branch (admin and executive looking at the whole company).
// #697 -- not what is ready at a hub for another branch: that is collected.
export function toLoadList(transfers, branchId, { howFilledAsker = false, shelfAt = () => () => 0, hubIds = [] } = {}) {
  return (Array.isArray(transfers) ? transfers : []).filter(t => {
    if (branchId != null && String(t.from_branch_id) !== String(branchId)) return false;
    const items = t.branch_transfer_items || [];
    if (hasReadyToLoad(t, items)) return !collectedAtHub(t, hubIds);
    return howFilledAsker && shelfFillable(t, items, shelfAt(t.from_branch_id));
  });
}

// A branch's open requests for its OWN shelf, which To make carries beside the
// orders (#580 part a).
export function openOwnShelfFor(transfers, branchId) {
  return (Array.isArray(transfers) ? transfers : []).filter(t => isOwnShelf(t)
    && ['awaiting_confirmation', 'approved', 'in_transit'].includes(t.status)
    && String(t.from_branch_id) === String(branchId));
}

// Out & owed's owed half: owed to a branch with nothing moving, and what a
// branch sent that came up short (#676) -- once per transfer, discrepancies
// first, then longest waiting.
export function owedList(transfers, loads, branchIds) {
  const seen = new Set();
  const rows = (Array.isArray(branchIds) ? branchIds : [])
    .flatMap(id => [...owedToBranch(transfers, loads, id), ...shortFromBranch(transfers, loads, id)])
    .filter(r => (seen.has(String(r.transfer.id)) ? false : seen.add(String(r.transfer.id))));
  const rank = k => (k === 'short' ? 0 : 1);
  return rows.sort((a, b) =>
    rank(a.kind) - rank(b.kind) ||
    new Date(a.transfer.requested_at || 0) - new Date(b.transfer.requested_at || 0));
}

// Every tab's list for ONE branch -- what the Transfers page shows a person
// looking at their own branch, which is what Stock Home's row counts.
// `reserve` is that branch's reserve rows (reserveCandidates()), or [] when the
// person cannot sign off and To make's count will not be shown to them.
// #697 -- `hubIds`, the branches whose hub switch is on: what is ready at one of
// them for this branch is in `hubPickups`, counted with To collect.
export function branchWorkLists({ transfers = [], openLoads = [], branchId, reserve = [], howFilledAsker = false, shelfAt, hubIds = [] } = {}) {
  const all = Array.isArray(transfers) ? transfers : [];
  const mine = all.filter(t =>
    String(t.from_branch_id) === String(branchId) || String(t.to_branch_id) === String(branchId));
  const toMake = productionQueue(mine, branchId);
  return {
    awaitingConfirm: mine.filter(t => isApprovable(t)),
    toMake,
    makeRows: productionRows({ orders: toMake, reserve, openOwnShelf: openOwnShelfFor(all, branchId) }),
    toLoad: toLoadList(mine, branchId, { howFilledAsker, shelfAt, hubIds }),
    elsewhere: movingElsewhere(openLoads, branchId),
    owed: owedList(mine, openLoads, [branchId]),
    collect: sortByPromised(toCollectFor(openLoads, branchId)),
    hubPickups: sortByPromised(hubPickupsFor(mine, branchId, hubIds).map(asHubPickup)),
    arriving: sortByPromised(arrivingAt(openLoads, branchId)),
  };
}

// ── WHICH OF IT IS THIS PERSON'S — #689 D18 and D19, with #699 D28 ──────────
//
// `canAct` -- may this person do this tab's job at THEIR OWN branch -- from the
// same entitlements the sheet uses; `counts` -- the tab pills' own numbers.
// The jobs are always at the person's own branch, whatever branch a page is
// showing, because an action is always taken at your own branch.
//
// D28: To load and To collect are "your job" only for the people switched on
// as drivers -- once anybody at the branch is. With nobody switched on,
// drivesHere() is true for everyone and this is the rule as it was.
//
// RULED BY COLBY at Session 79.22b.5.1 (R4, "i approve"), amending D28: THE
// SWITCH NARROWS THE JOB FOR TEAM MEMBERS ONLY. A manager, executive or admin
// keeps loads and pickups as their work whoever drives, because for them a
// waiting pickup is work to DIRECT -- the supervisor's queue SAP's warehouse
// monitor and ServiceNow's group queues both put in front of the person
// assigning it. A pre-filled driver name is a different question and stays
// presserDriver()'s: a manager who does not drive is never named as one.
// #682, Session 79.22b.6c.2b (665 D18, the corrections gate's F3) -- AND THE
// TRUCK QUESTIONS this person may answer: each is something to do on Awaiting
// confirmation, so it opens there and its pill counts it. The page passes only
// the ones canAnswerTruckExtra() lets this person answer. Stock Home passes none,
// so its row is unchanged (Colby's R1 at 79.22b.6c.2b: Transfers only).
export function transferJobs({ lists, openLoads = [], myBranch, profile, auth, drivers = [], truckQuestions = [] } = {}) {
  const l = lists || {};
  const sentFromMine = t => String(t.from_branch_id) === String(myBranch);
  const driving = isAdminManagerOrExecutive(auth || {})
    || drivesHere(drivers, auth?.user?.id ?? profile?.id ?? null);
  // #697 -- a pickup at a hub is the receiving branch's job.
  const hubForMine = (l.hubPickups || []).some(p => String(p.to_branch_id) === String(myBranch));
  return {
    canAct: {
      confirm: (l.awaitingConfirm || []).some(t => canApproveTransfer(profile, t))
        || (Array.isArray(truckQuestions) && truckQuestions.length > 0),
      to_make: canSignOffProduction(auth) && (l.toMake || []).some(sentFromMine),
      to_load: driving && (l.toLoad || []).some(sentFromMine),
      moving: (l.owed || []).some(r => canCloseTransferShort(profile, r.transfer)),
      collect: driving && (toCollectFor(openLoads, myBranch).length > 0 || hubForMine),
      count_in: arrivingAt(openLoads, myBranch).length > 0,
    },
    counts: {
      confirm: (l.awaitingConfirm || []).length + (Array.isArray(truckQuestions) ? truckQuestions.length : 0),
      to_make: (l.makeRows || []).length,
      to_load: (l.toLoad || []).length,
      moving: (l.elsewhere || []).length + (l.owed || []).length,
      collect: (l.collect || []).length + (l.hubPickups || []).length,
      count_in: (l.arriving || []).length,
    },
  };
}

// ── STOCK HOME'S ROW — gate 665 D19, Screen 7, and the F2 copy table ────────
//
// The chips, in the order a transfer travels, each shown only when the person
// can do that job AND the branch has some of it. "Out & owed" has no chip: the
// copy table names five, and writing a shortfall off is a manager's decision
// made on the transfer, not a job waiting on a shelf.
export const SHORTCUT_TABS = ['confirm', 'to_make', 'to_load', 'collect', 'count_in'];
const SHORTCUT_WORDS = {
  confirm: 'to agree',
  to_make: 'to make',
  to_load: 'to load',
  collect: 'to collect',
  count_in: 'to count in',
};

// Each chip's dot is its tab's stage colour, so a chip and the tab it opens
// read as one thing. THE SAME VALUES AS TransfersPage's TRANSFER_TABS -- a page
// may import a lib and never the other way round, so they are held here and
// transferWork.test.js fails if the two ever differ.
export const SHORTCUT_DOT = {
  confirm: '#7F77DD',
  to_make: '#EF9F27',
  to_load: '#E2761B',
  collect: '#378ADD',
  count_in: '#1D9E75',
};

export function shortcutChips({ canAct = {}, counts = {} } = {}) {
  return SHORTCUT_TABS
    .filter(tab => canAct[tab] && Number(counts[tab]) > 0)
    .map(tab => ({ tab, n: Number(counts[tab]), label: `${Number(counts[tab])} ${SHORTCUT_WORDS[tab]}` }));
}

// "Transfer work" on a phone, "Transfer work at Houston" on a desk.
export function shortcutHeading(branchName, onDesk) {
  return onDesk && branchName ? `Transfer work at ${branchName}` : 'Transfer work';
}

// The statuses any of the five lists can hold, plus a request ended "nothing
// more is coming" while a truck was still out, whose truck still arrives
// (#600).
export const WORK_STATUSES = ['requested', 'awaiting_confirmation', 'approved', 'in_transit', 'closed_unfilled'];

// Everything the row needs, for ONE branch, read the way the Transfers page
// reads it. `ok: false` on any failed read, and the caller then draws nothing:
// the gate's error state is "the row is absent and Stock Home works as today",
// never a zero that reads as "no work".
//
// #608 / gate 675 D15, Session 79.22b.5.3 -- READ FROM THE WORK IN HAND, THE
// TRANSFERS PAGE'S OWN RULE. Asking for the statuses above by name brought back
// every "nothing more coming" transfer the branch was ever part of - a finished
// status - so the list grew with history. transfersInHand() keeps one only
// while a truck of it is still out. And A CUT LIST IS A FAILED READ HERE
// (cutOff(), D17): counts missing some of the work would be a confident wrong
// number on Stock Home, which is worse than the row being absent.
export async function fetchTransferWork({ branchId, auth } = {}) {
  if (branchId == null) return { ok: false };
  const [work, drivers, { data: hubs, error: hubsError }] = await Promise.all([
    transfersInHand('*, branch_transfer_items(*)')
      .in('status', WORK_STATUSES)
      .or(`from_branch_id.eq.${Number(branchId)},to_branch_id.eq.${Number(branchId)}`),
    fetchBranchDrivers(branchId),
    // #697 -- which branches are hubs, so a pickup at one counts as To collect.
    supabase.from('branches').select('id, is_transfer_hub').eq('is_transfer_hub', true),
  ]);
  if (work.error || hubsError || cutOff(work)) return { ok: false };
  const hubIds = hubIdsOf(hubs);
  const transfers = work.data || [];
  const trucks = await fetchOpenLoads(branchId, transferIdsWithLoadsToWatch(transfers));
  if (trucks.error || cutOff(trucks)) return { ok: false };
  const openLoads = trucks.data;

  // To make's reserve rows count only for someone who can sign off, and the
  // shelf a made-to-order request could be filled from only for someone who
  // says how a line was filled -- so the production read is skipped for
  // everybody else, which is most people on most days.
  const signs = canSignOffProduction(auth);
  const howFilledAsker = canStateHowFilled(auth || {});
  let reserve = [];
  let shelfAt = () => () => 0;
  if (signs || howFilledAsker) {
    const prod = await fetchProductionData(branchId);
    // #608 -- a cut or failed shelf read is a failed read here too: a To make
    // count missing some low shelves would be a confident wrong number.
    if (!prod.ok) return { ok: false };
    if (signs) {
      reserve = reserveCandidates({ items: prod.items, stock: prod.stock, categories: prod.categories, branchId });
    }
    const shelf = Object.fromEntries((prod.stock || []).map(s => [String(s.item_id), s]));
    shelfAt = b => itemId => (String(b) === String(branchId) ? (Number(shelf[String(itemId)]?.qty_on_hand) || 0) : 0);
  }
  const lists = branchWorkLists({ transfers, openLoads: openLoads || [], branchId, reserve, howFilledAsker, shelfAt, hubIds });
  return { ok: true, lists, openLoads: openLoads || [], drivers };
}
